import {
  Directive,
  Input,
  QueryList,
  ContentChildren,
  AfterViewInit,
} from '@angular/core';
import { TooltipDirective } from '../../tooltip/tooltip.directive';

@Directive({
  selector: '[sag-ui-range-base-multi]',
})
export class RangeBaseMultiDirective implements AfterViewInit {
  private thumbs: HTMLElement[] = [];
  private inputs: HTMLInputElement[] = [];
  private track!: HTMLElement;

  @Input() min = 0;
  @Input() max = 100;
  @Input() step = 1;

  @Input('thumbs')
  set rangeThumbs(elements: HTMLElement[]) {
    this.thumbs = elements;
    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('mousedown', (event) =>
        this.onMouseDown(event, index)
      );
    });
  }

  @Input('track')
  set rangeTrack(element: HTMLElement) {
    this.track = element;
    this.track.addEventListener('click', this.onClick.bind(this));
  }

  @Input('inputs')
  set rangeInput(element: HTMLInputElement[]) {
    this.inputs = element;
    this.inputs.forEach((input, index) => {
      input.addEventListener('input', () => this.updateThumbPosition(index));
    });
    this.updateAllThumbPositions();
  }

  @ContentChildren(TooltipDirective, { descendants: true })
  tooltips!: QueryList<TooltipDirective>;

  ngAfterViewInit(): void {
    this.updateAllThumbPositions();
  }

  private updateThumbPosition(index: number): void {
    const input = this.inputs[index];
    const thumb = this.thumbs[index];
    const tooltip = this.tooltips ? this.tooltips.toArray()[index] : null;

    const percent =
      ((input.valueAsNumber - this.min) / (this.max - this.min)) * 100;
    thumb.style.left = `${percent}%`;

    if (tooltip) {
      tooltip.tooltip = input.value;
    }
  }

  private updateAllThumbPositions(): void {
    this.inputs.forEach((_, index) => this.updateThumbPosition(index));
  }

  protected onMouseDown(event: MouseEvent, index: number): void {
    const input = this.inputs[index];
    if (input.disabled) return;

    event.stopPropagation();
    this.initDrag(event, input, this.thumbs[index]);
  }

  protected onClick(event: MouseEvent): void {
    event.stopPropagation();

    const distances = this.thumbs.map((thumb) => {
      const rect = thumb.getBoundingClientRect();
      return Math.abs(event.clientX - (rect.left + rect.width / 2));
    });
    const closestThumbIndex = distances.indexOf(Math.min(...distances));

    this.moveThumb(
      event,
      this.inputs[closestThumbIndex],
      this.thumbs[closestThumbIndex]
    );
  }

  private initDrag(
    _: MouseEvent,
    input: HTMLInputElement,
    thumb: HTMLElement
  ): void {
    const { left, width } = this.track.getBoundingClientRect();

    const onMouseMove = (e: MouseEvent) => {
      if (input.disabled) return;
      this.moveThumb(e, input, thumb, left, width);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  private moveThumb(
    event: MouseEvent,
    input: HTMLInputElement,
    _: HTMLElement,
    left: number = this.track.getBoundingClientRect().left,
    width: number = this.track.getBoundingClientRect().width
  ): void {
    const x = event.clientX - left;
    const percent = Math.max(0, Math.min(1, x / width));
    const newValue = Math.round(this.min + percent * (this.max - this.min));
    const stepValue = Math.round(newValue / this.step) * this.step;
    input.valueAsNumber = stepValue;
    this.updateThumbPosition(this.inputs.indexOf(input));
  }
}
